import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, Heart, Settings, Play, Trash2, Calendar,
  CheckCircle, Clock, Star, Users, Plus, History,
  UserPlus, UserMinus, Bell, Camera, LogOut
} from "lucide-react";
import { Button } from "./ui/button";
import { getWatchHistory, deleteWatchHistoryEntry, clearWatchHistory } from "../services/watchHistory";
import { ProfilePicturePicker } from "./ProfilePicturePicker";



import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const UserAccountPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("myteams");
  const [favorites, setFavorites] = useState([]);
  const [favoriteTeams, setFavoriteTeams] = useState([]);
  const [followedTeams, setFollowedTeams] = useState([]);
  const [followedTeamIds, setFollowedTeamIds] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [watchHistory, setWatchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicturePicker, setShowPicturePicker] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) {
      navigate("/login-user?return=/account");
      return;
    }

    const fetchUserData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Get user profile
        const userRes = await axios.get(`${API}/users/me`, { headers });
        setUser(userRes.data);

        // Get favorites
        const favRes = await axios.get(`${API}/users/favorites`, { headers });
        setFavorites(favRes.data.favorites || []);

        // Get favorite teams
        try {
          const favTeamsRes = await axios.get(`${API}/users/preferences/favorite-teams`, { headers });
          setFavoriteTeams(favTeamsRes.data.teams || []);
        } catch (err) {
          console.log("No favorite teams yet");
        }

        // Get followed teams
        try {
          const followRes = await axios.get(`${API}/users/preferences/followed-teams`, { headers });
          setFollowedTeams(followRes.data.teams || []);
          setFollowedTeamIds(followRes.data.followed_teams || []);
        } catch (err) {
          console.log("No followed teams yet");
        }

        // Get all available teams
        try {
          const teamsRes = await axios.get(`${API}/teams`);
          setAllTeams(teamsRes.data || []);
        } catch (err) {
          console.log("Could not fetch teams");
        }

        // Get watch history
        try {
          const historyData = await getWatchHistory(50);
          setWatchHistory(historyData);
        } catch (err) {
          console.log("Could not fetch watch history");
        }

      } catch (err) {
        console.error("Error fetching user data:", err);
        if (err.response?.status === 401) {
          localStorage.removeItem("userToken");
          localStorage.removeItem("userData");
          navigate("/login-user?return=/account");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userData");
    window.dispatchEvent(new Event("storage"));
    navigate("/");
  };

  const handleRemoveFavorite = async (recordingId) => {
    const token = localStorage.getItem("userToken");
    try {
      await axios.delete(`${API}/users/favorites/${recordingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(favorites.filter(f => f.id !== recordingId));
    } catch (err) {
      console.error("Error removing favorite:", err);
    }
  };

  const handleAddFavoriteTeam = async (teamId) => {
    const token = localStorage.getItem("userToken");
    try {
      await axios.post(`${API}/users/preferences/favorite-teams/add/${teamId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh favorite teams
      const res = await axios.get(`${API}/users/preferences/favorite-teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavoriteTeams(res.data.teams || []);
    } catch (err) {
      console.error("Error adding favorite team:", err);
    }
  };

  const handleRemoveFavoriteTeam = async (teamId) => {
    const token = localStorage.getItem("userToken");
    try {
      await axios.delete(`${API}/users/preferences/favorite-teams/remove/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavoriteTeams(favoriteTeams.filter(t => t.id !== teamId));
    } catch (err) {
      console.error("Error removing favorite team:", err);
    }
  };

  const handleFollowToggle = async (teamId, nowFollowed) => {
    const token = localStorage.getItem("userToken");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (nowFollowed) {
        setFollowedTeamIds(prev => [...prev, teamId]);
        // Find team from allTeams and add to followedTeams
        const team = allTeams.find(t => t.id === teamId);
        if (team) setFollowedTeams(prev => [...prev, team]);
      } else {
        setFollowedTeamIds(prev => prev.filter(id => id !== teamId));
        setFollowedTeams(prev => prev.filter(t => t.id !== teamId));
      }
    } catch (err) {
      console.error("Error toggling follow:", err);
    }
  };

  const handleRemoveHistoryEntry = async (contentId) => {
    const success = await deleteWatchHistoryEntry(contentId);
    if (success) {
      setWatchHistory(watchHistory.filter(h => h.content_id !== contentId));
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm("Clear all watch history? This cannot be undone.")) return;
    const success = await clearWatchHistory();
    if (success) {
      setWatchHistory([]);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatProgress = (progress, duration) => {
    if (!duration) return '0%';
    return `${Math.round((progress / duration) * 100)}%`;
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505]">
        
        
        <div className="pt-8 pb-16 px-4 flex items-center justify-center">
          <div className="text-zinc-500">Loading...</div>
        </div>
        
      </main>
    );
  }

  const tabs = [
    { id: "myteams", label: "My Teams", icon: Star },
    { id: "history", label: "Watch History", icon: History },
    { id: "favorites", label: "Favorites", icon: Heart },
    { id: "settings", label: "Settings", icon: Settings }
  ];

  return (
    <main className="min-h-screen bg-[#050505]">
      
      
      
      <div className="pt-8 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-white/10 p-6 mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setShowPicturePicker(true)}
                  className="relative group"
                  data-testid="profile-picture-btn"
                >
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center overflow-hidden border-2 border-white/20 group-hover:border-white/40 transition-colors">
                    {user?.profile_picture ? (
                      <img 
                        src={user.profile_picture} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-8 h-8 text-white" />
                    )}
                  </div>
                  <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="w-5 h-5 text-white" />
                  </div>
                </button>
                <div>
                  <h1 className="font-secondary text-xl font-bold uppercase tracking-tight text-white">
                    {user?.name}
                  </h1>
                  <p className="text-zinc-500 text-sm">@{user?.username}</p>
                  <p className="text-zinc-600 text-xs mt-1">{user?.email}</p>
                </div>
              </div>
              <Button
                onClick={handleLogout}
                className="bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white font-secondary text-xs px-4 py-2 h-auto rounded-none"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </motion.div>

          {/* Profile Picture Picker Modal */}
          <AnimatePresence>
            {showPicturePicker && (
              <ProfilePicturePicker
                currentPicture={user?.profile_picture}
                onUpdate={(newPicture) => {
                  setUser({ ...user, profile_picture: newPicture });
                }}
                onClose={() => setShowPicturePicker(false)}
              />
            )}
          </AnimatePresence>

          {/* Tabs */}
          <div className="flex border-b border-white/10 mb-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 font-secondary text-xs md:text-sm font-bold uppercase tracking-widest transition-colors duration-200 border-b-2 -mb-[2px] whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-white border-white'
                    : 'text-zinc-500 border-transparent hover:text-zinc-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            {/* MY TEAMS TAB */}
            {activeTab === "myteams" && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl md:text-2xl font-bold uppercase tracking-tight text-white">
                    My Teams
                  </h2>
                </div>

                {/* Followed Teams */}
                <div className="mb-8">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-4 flex items-center gap-2">
                    <Bell className="w-3.5 h-3.5" />
                    Following ({followedTeams.length})
                  </h3>
                  {followedTeams.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {followedTeams.map((team) => (
                        <div
                          key={team.id}
                          className="bg-zinc-900/50 border border-orange-500/20 p-4 flex items-center gap-4"
                          data-testid={`followed-team-${team.id}`}
                        >
                          <div 
                            className="w-14 h-14 rounded flex items-center justify-center flex-shrink-0"
                            style={{ 
                              backgroundColor: (team.primary_color || '#FF5722') + '20',
                              borderLeft: `4px solid ${team.primary_color || '#FF5722'}`
                            }}
                          >
                            {team.logo_url ? (
                              <img src={team.logo_url} alt={team.name} className="w-10 h-10 object-contain" />
                            ) : (
                              <span className="text-lg font-bold" style={{ color: team.primary_color || '#FF5722' }}>
                                {team.abbreviation || team.name?.slice(0, 2)}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-bold truncate">{team.name}</h3>
                            <p className="text-zinc-500 text-xs">
                              {team.mascot && `${team.mascot} • `}
                              {[team.city, team.state].filter(Boolean).join(", ")}
                            </p>
                            {team.conference && (
                              <span className="text-[10px] px-2 py-0.5 bg-white/10 text-zinc-400 inline-block mt-1">
                                {team.conference}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              const token = localStorage.getItem("userToken");
                              axios.delete(`${API}/users/preferences/unfollow-team/${team.id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              }).then(() => handleFollowToggle(team.id, false));
                            }}
                            className="p-2 text-orange-500 hover:text-red-500 transition-colors"
                            data-testid={`unfollow-team-${team.id}`}
                            title="Unfollow"
                          >
                            <UserMinus className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-zinc-900/30 border border-white/5 p-6 text-center mb-6">
                      <Bell className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                      <p className="text-zinc-500 text-sm">Follow teams below to get notifications about their games</p>
                    </div>
                  )}
                </div>

                {/* Current Favorite Teams */}
                {favoriteTeams.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-4">
                      Your Favorite Teams ({favoriteTeams.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {favoriteTeams.map((team) => (
                        <div
                          key={team.id}
                          className="bg-zinc-900/50 border border-white/10 p-4 flex items-center gap-4"
                          data-testid={`favorite-team-${team.id}`}
                        >
                          <div 
                            className="w-14 h-14 rounded flex items-center justify-center flex-shrink-0"
                            style={{ 
                              backgroundColor: (team.primary_color || '#FF5722') + '20',
                              borderLeft: `4px solid ${team.primary_color || '#FF5722'}`
                            }}
                          >
                            {team.logo_url ? (
                              <img src={team.logo_url} alt={team.name} className="w-10 h-10 object-contain" />
                            ) : (
                              <span className="text-lg font-bold" style={{ color: team.primary_color || '#FF5722' }}>
                                {team.abbreviation || team.name?.slice(0, 2)}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-bold truncate">{team.name}</h3>
                            <p className="text-zinc-500 text-xs">
                              {team.mascot && `${team.mascot} • `}
                              {[team.city, team.state].filter(Boolean).join(", ")}
                            </p>
                            {team.conference && (
                              <span className="text-[10px] px-2 py-0.5 bg-white/10 text-zinc-400 inline-block mt-1">
                                {team.conference}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveFavoriteTeam(team.id)}
                            className="p-2 text-zinc-500 hover:text-red-500 transition-colors"
                            data-testid={`remove-favorite-team-${team.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Available Teams - with Follow buttons */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-4">
                    All Teams
                  </h3>
                  {allTeams.length === 0 ? (
                    <div className="bg-zinc-900/50 border border-white/10 p-8 text-center">
                      <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-500 mb-2">No teams available yet</p>
                      <p className="text-zinc-600 text-sm">
                        Teams will be added by the network administrators
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {allTeams
                        .filter(team => team.name?.trim().toUpperCase() !== "TBD")
                        .map((team) => {
                          const isFollowed = followedTeamIds.includes(team.id);
                          const isFavorited = favoriteTeams.some(ft => ft.id === team.id);
                          return (
                            <div
                              key={team.id}
                              className="bg-zinc-900/30 border border-white/5 hover:border-white/20 p-3 flex items-center gap-3 transition-colors"
                              data-testid={`team-row-${team.id}`}
                            >
                              <div 
                                className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: (team.primary_color || '#FF5722') + '15' }}
                              >
                                {team.logo_url ? (
                                  <img src={team.logo_url} alt={team.name} className="w-7 h-7 object-contain" />
                                ) : (
                                  <span className="text-sm font-bold" style={{ color: team.primary_color || '#FF5722' }}>
                                    {team.abbreviation || team.name?.slice(0, 2)}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-white text-sm font-medium truncate">{team.name}</h3>
                                <p className="text-zinc-600 text-xs">
                                  {team.conference || team.mascot || ""}
                                </p>
                              </div>
                              <div className="flex items-center gap-1">
                                {/* Follow Button */}
                                <button
                                  onClick={async () => {
                                    const token = localStorage.getItem("userToken");
                                    const headers = { Authorization: `Bearer ${token}` };
                                    if (isFollowed) {
                                      await axios.delete(`${API}/users/preferences/unfollow-team/${team.id}`, { headers });
                                      handleFollowToggle(team.id, false);
                                    } else {
                                      await axios.post(`${API}/users/preferences/follow-team/${team.id}`, null, { headers });
                                      handleFollowToggle(team.id, true);
                                    }
                                  }}
                                  className={`p-1.5 rounded transition-colors ${
                                    isFollowed 
                                      ? 'bg-orange-500/20 text-orange-500 hover:bg-red-500/20 hover:text-red-400' 
                                      : 'bg-white/5 text-zinc-500 hover:text-orange-500 hover:bg-orange-500/10'
                                  }`}
                                  title={isFollowed ? "Unfollow" : "Follow for notifications"}
                                  data-testid={`follow-toggle-${team.id}`}
                                >
                                  {isFollowed ? <UserMinus className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                                </button>
                                {/* Favorite Button */}
                                {!isFavorited && (
                                  <button
                                    onClick={() => handleAddFavoriteTeam(team.id)}
                                    className="p-1.5 bg-white/5 hover:bg-orange-500/20 rounded transition-colors"
                                    title="Add to favorites"
                                    data-testid={`add-favorite-${team.id}`}
                                  >
                                    <Plus className="w-4 h-4 text-zinc-500 hover:text-orange-500" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                  {allTeams.length > 0 && favoriteTeams.length === allTeams.length && (
                    <div className="bg-green-500/10 border border-green-500/20 p-4 text-center mt-4">
                      <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                      <p className="text-green-500 text-sm">You&apos;ve favorited all available teams!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* FAVORITES TAB */}
            {activeTab === "favorites" && (
              <div>
                <h2 className="font-secondary text-lg font-bold uppercase tracking-tight text-white mb-4">
                  Favorite Games
                </h2>
                {favorites.length === 0 ? (
                  <div className="bg-zinc-900/50 border border-white/10 p-8 text-center">
                    <Heart className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500 mb-4">You haven&apos;t favorited any games yet</p>
                    <Link to="/watch">
                      <Button className="bg-white text-black hover:bg-zinc-200 font-secondary text-xs px-4 py-2 h-auto rounded-none">
                        Browse On Demand
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {favorites.map((recording) => (
                      <div
                        key={recording.id}
                        className="bg-zinc-900/50 border border-white/10 p-4 flex items-center gap-4"
                      >
                        <div className="w-32 h-20 bg-zinc-800 flex-shrink-0 overflow-hidden">
                          {recording.thumbnail_url ? (
                            <img 
                              src={recording.thumbnail_url} 
                              alt={recording.title} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="w-8 h-8 text-zinc-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-secondary text-white font-bold truncate">{recording.title}</h3>
                          <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                            <span>{recording.sport}</span>
                            {recording.recorded_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(recording.recorded_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link to={`/on-demand/${recording.id}`}>
                            <Button className="bg-white text-black hover:bg-zinc-200 font-secondary text-xs px-4 py-2 h-auto rounded-none">
                              <Play className="w-3 h-3 mr-1" />
                              Watch
                            </Button>
                          </Link>
                          <button
                            onClick={() => handleRemoveFavorite(recording.id)}
                            className="p-2 text-zinc-500 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* SETTINGS TAB */}
            {activeTab === "settings" && (
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold uppercase tracking-tight text-white mb-4">
                  Account Settings
                </h2>
                <div className="bg-zinc-900/50 border border-white/10 p-6 space-y-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Full Name
                    </label>
                    <p className="text-white">{user?.name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Email
                    </label>
                    <p className="text-white">{user?.email}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Username
                    </label>
                    <p className="text-white">@{user?.username}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Phone
                    </label>
                    <p className="text-white">{user?.phone}</p>
                  </div>
                  
                  {/* Member Info */}
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-zinc-500 text-xs">
                      Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      
    </main>
  );
};

export default UserAccountPage;
